import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import {
  PostRegisterInventoryTransfer,
  TRegisterInventoryDirection,
} from "api/rpc/2024-04/facilityAdmin/product/registerInventory";
import { variantHasRegisterQuantity } from "helpers/registerInventory";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { IProduct } from "redux/reducers/models/product";
import Sheet from "components/sheet/Sheet";
import Radio from "components/radio";
import ProductSelect from "components/productModal/ProductSelect";

interface IRegisterStockTransferModalProps {
  open: boolean;
  registerId: number;
  registerTitle?: string;
  onCancel: () => void;
  onSuccess?: () => void;
}

export default function RegisterStockTransferModal(props: IRegisterStockTransferModalProps) {
  const { open, registerId, registerTitle, onCancel, onSuccess } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [direction, setDirection] = useState<TRegisterInventoryDirection>("to_register");
  const [selectedProducts, setSelectedProducts] = useState<IProduct[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setDirection("to_register");
      setSelectedProducts([]);
      setSaving(false);
    }
  }, [open]);

  function handleDirectionChange(value: TRegisterInventoryDirection) {
    setDirection(value);
    setSelectedProducts([]);
  }

  function handleProductSelect(products: IProduct[]) {
    const updatedProducts: IProduct[] = products.map(product => ({
      ...product,
      variants: (product.variants ?? []).map(variant => ({
        ...variant,
        quantity: variant.quantity == null ? 1 : variant.quantity,
      })),
    }));
    setSelectedProducts(updatedProducts);
  }

  function getTransferVariants(): Array<{ variant_id: number; quantity: number }> {
    const variants: Array<{ variant_id: number; quantity: number }> = [];

    selectedProducts.forEach(product => {
      (product.variants ?? []).forEach(variant => {
        const variantId = Number(variant.id);
        const quantity = Number(variant.quantity);
        if (variantId > 0 && quantity > 0) {
          variants.push({ variant_id: variantId, quantity });
        }
      });
    });

    return variants;
  }

  async function handleTransfer() {
    const variants = getTransferVariants();
    if (!registerId || variants.length === 0) {
      return;
    }

    setSaving(true);
    const resp = await PostRegisterInventoryTransfer(
      {
        register_id: registerId,
        direction,
        variants,
      },
      false,
    );

    if (resp.status !== StatusCode.OK) {
      dispatch(
        showError((resp.data.message as string) ?? t("Error transferring inventory")),
      );
      setSaving(false);
      return;
    }

    dispatch(showSuccess(t("Inventory transferred successfully")));
    setSaving(false);
    if (onSuccess) {
      onSuccess();
      return;
    }
    onCancel();
  }

  const transferVariants = getTransferVariants();

  return (
    <Sheet
      open={open}
      title={t("Transfer Inventory")}
      supportText={registerTitle}
      size="medium"
      closable
      overflow
      okText={direction === "to_register" ? t("Transfer to Register") : t("Return to Facility")}
      cancelText={t("Cancel")}
      okDisabled={transferVariants.length === 0}
      okLoading={saving}
      onOk={() => void handleTransfer()}
      onCancel={onCancel}
    >
      <div className="mb-4">
        <Radio.Group
          name="register-stock-direction"
          value={direction}
          onChange={handleDirectionChange}
        >
          <Radio.Button value="to_register">{t("Transfer to Register")}</Radio.Button>
          <Radio.Button value="to_facility">{t("Return to Facility")}</Radio.Button>
        </Radio.Group>
        <p className="mt-2">
          {direction === "to_register"
            ? t("Move unassigned facility stock onto this register")
            : t("Return leftover stock from this register to the facility")}
        </p>
      </div>

      <ProductSelect
        key={direction}
        selectedProducts={selectedProducts}
        onChange={handleProductSelect}
        search
        getProductsParams={{
          track_inventory: true,
          ...(direction === "to_facility" ? { register_id: registerId, inventory: true } : {}),
        }}
        filterVariants={
          direction === "to_facility" ? variant => variantHasRegisterQuantity(variant, registerId) : undefined
        }
        variantInput={{ key: "quantity", placeholder: t("Quantity") }}
        vendorFilter
        maxHeight="400px"
      />
    </Sheet>
  );
}
